import "./App.css";

// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { routes } from "./routes";
import { LayoutProvider } from "./lib/contexts/layout.context";
import { BrowserRouter, useRoutes } from "react-router-dom";
import { usePageViewTracker } from "./lib/analytics/usePageViewTracker.hook";
import { Toaster } from "sonner";
import LoadingSpinner from "./components/loadingSpinner.component";
import { Suspense } from "react";
import { CookiesProvider } from "react-cookie";
import { AuthProvider } from "./lib/contexts/auth.context";
import * as Sentry from "@sentry/react";
import { LiquidGlassBackground } from "./components/liquidGlassBackground.component";

Sentry.init({
	dsn: "https://fa00d24fcdcf5c9f99935dedea4fd04e@o4508817033723904.ingest.de.sentry.io/4510436601495632",
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration(),
	],
	// Tracing
	tracesSampleRate: 1.0, //  Capture 100% of the transactions
	// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
	// we are currently only interested in the api and the app (release environment)
	tracePropagationTargets: [
		"localhost",
		/^https:\/\/api.app.podhive\.de/,
		/^https:\/\/app.podhive\.de/,
	],
	// Session Replay
	replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
	enabled: process.env.NODE_ENV === "production",
});

const firebaseConfig = JSON.parse(
	import.meta.env.VITE_FIREBASE_CONFIG as string,
);

// this will prevent console.log from being called in production
if (process.env.NODE_ENV === "production") {
	console.log = () => {};
}

if (!firebaseConfig) {
	throw new Error("Firebase service account not found");
}

console.log(firebaseConfig);
// Initialize Firebase
initializeApp(firebaseConfig);

/**
 * Main App component
 *
 * Provides global context providers and routing.
 * The routing structure uses layout-based composition:
 * - AuthLayout for unauthenticated pages
 * - MainLayout for authenticated pages
 *
 * This approach is cleaner and more maintainable than conditionally
 * rendering UI elements based on route checks.
 */
function App() {
	return (
		<BrowserRouter>
			<LiquidGlassBackground />
			<CookiesProvider>
				<AuthProvider>
					<LayoutProvider>
						<Suspense fallback={<LoadingSpinner />}>
							<AppRoutes />
						</Suspense>
					</LayoutProvider>
				</AuthProvider>
			</CookiesProvider>
			<Toaster />
		</BrowserRouter>
	);
}

/**
 * AppRoutes component that renders the route configuration
 */
function AppRoutes() {
	// Track page views on every client-side route change
	usePageViewTracker();
	const element = useRoutes(routes);
	return element;
}

export default App;
